import { createRouter, createWebHistory } from "vue-router";
import ROUTES from "@/config/ROUTES.ts";
import { type LocalizedRoute, localizedRoutes } from "@/extensions/langRouter";
import i18n from "@/i18n";
import { usePageStore } from "@/stores";
import type { Pinia } from "pinia";

async function buildRoutes(pinia: Pinia): Promise<LocalizedRoute[]> {
  const pageStore = usePageStore(pinia);
  let pages: Record<string, Page> = {};

  try {
    pages = await pageStore.all();
  } catch (error) {
    console.warn("Failed to load pages for dynamic routing, using default routes", error);
  }

  const getPath = (
    template: string,
    defaultPath: string,
    defaultEnPath?: string,
  ): { path: string; paths?: { en: string }; pageTitle?: string } => {
    const page = pages[template];
    if (!page?.slug) {
      return defaultEnPath
        ? { path: defaultPath, paths: { en: defaultEnPath } }
        : { path: defaultPath };
    }

    const frSlug = page.slug.fr || defaultPath.substring(1); // Remove leading '/'
    const enSlug = page.slug.en || defaultEnPath?.substring(1) || defaultPath.substring(1);

    return {
      path: `/${frSlug}`,
      paths: { en: `/${enSlug}` },
      pageTitle: page.title,
    };
  };

  const routes: LocalizedRoute[] = [
    {
      path: "/",
      name: ROUTES.HOME,
      component: () => import("@/views/HomePage.vue"),
      meta: {
        title: "page_titles.home",
      },
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("about", "/a-propos", "/about");
        return {
          ...pathConfig,
          name: ROUTES.ABOUT,
          component: () => import("@/views/AboutPage.vue"),
          meta: {
            title: pageTitle || "page_titles.about",
          },
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("careers", "/carrieres", "/careers");
        return {
          ...pathConfig,
          name: ROUTES.CAREERS,
          component: () => import("@/views/CareersPage.vue"),
          meta: {
            title: pageTitle || "page_titles.careers",
          },
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("documents", "/ressources", "/resources");
        return {
          ...pathConfig,
          name: ROUTES.DOCUMENT.BASE,
          children: [
            {
              path: "",
              name: ROUTES.DOCUMENT.INDEX,
              component: () => import("@/views/Document/DocumentIndex.vue"),
              meta: {
                title: pageTitle || "page_titles.resources",
              },
            },
          ],
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("events", "/evenements", "/events");
        return {
          ...pathConfig,
          name: ROUTES.EVENT.BASE,
          children: [
            {
              path: "",
              name: ROUTES.EVENT.INDEX,
              component: () => import("@/views/Event/EventIndex.vue"),
              meta: {
                title: pageTitle || "page_titles.events",
              },
            },
            {
              path: ":slug",
              name: ROUTES.EVENT.SHOW,
              component: () => import("@/views/Event/EventShow.vue"),
              meta: {
                title: pageTitle || "page_titles.events",
              },
            },
          ],
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("funding", "/financement", "/funding");
        return {
          ...pathConfig,
          name: ROUTES.FUNDING,
          component: () => import("@/views/FundingPage.vue"),
          meta: {
            title: pageTitle || "page_titles.funding",
          },
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("news", "/nouvelles", "/news");
        return {
          ...pathConfig,
          name: ROUTES.NEWS.BASE,
          children: [
            {
              path: "",
              name: ROUTES.NEWS.INDEX,
              component: () => import("@/views/News/NewsIndex.vue"),
              meta: {
                title: pageTitle || "page_titles.news",
              },
            },
            {
              path: ":slug",
              name: ROUTES.NEWS.SHOW,
              component: () => import("@/views/News/NewsShow.vue"),
              meta: {
                title: pageTitle || "page_titles.news",
              },
            },
          ],
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("posts", "/publications", "/posts");
        return {
          ...pathConfig,
          name: ROUTES.POST.BASE,
          children: [
            {
              path: "",
              name: ROUTES.POST.INDEX,
              component: () => import("@/views/Post/PostIndex.vue"),
              meta: {
                title: pageTitle || "page_titles.posts",
              },
            },
          ],
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath(
          "policies",
          "/politique-de-confidentialite",
          "/privacy-policy",
        );
        return {
          ...pathConfig,
          name: ROUTES.POLICIES,
          component: () => import("@/views/PoliciesPage.vue"),
          meta: {
            title: pageTitle || "page_titles.policies",
          },
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle, ...pathConfig } = getPath("projects", "/projets", "/projects");
        return {
          ...pathConfig,
          name: ROUTES.PROJECTS,
          component: () => import("@/views/ProjectsPage.vue"),
          meta: {
            title: pageTitle || "page_titles.projects",
          },
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle: _pageTitle, ...pathConfig } = getPath(
          "innovation",
          "/innovation",
          "/innovation",
        );
        return {
          ...pathConfig,
          name: ROUTES.INNOVATION,
          component: () => import("@/views/InnovationPage.vue"),
          meta: {
            title: "page_titles.innovation",
          },
        };
      })(),
    },
    {
      ...(() => {
        const { pageTitle: _pageTitle, ...pathConfig } = getPath("team", "/equipe", "/team");
        return {
          ...pathConfig,
          name: ROUTES.TEAM,
          component: () => import("@/views/TeamPage.vue"),
          meta: {
            title: "page_titles.team",
          },
        };
      })(),
    },
    {
      path: "/:pathMatch(.*)*",
      name: "catch-all",
      component: () => import("@/views/NotFound.vue"),
      meta: {
        title: "page_titles.not_found",
      },
    },
  ];
  return routes;
}

export async function createAppRouter(pinia: Pinia) {
  const routes = await buildRoutes(pinia);

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    linkActiveClass: "-active",
    linkExactActiveClass: "-exact-active",
    routes: localizedRoutes(routes, ["fr", "en"]),
    scrollBehavior(to) {
      if (to.hash) {
        return { el: to.hash, behavior: "smooth" };
      }

      return new Promise((resolve) => setTimeout(() => resolve({ top: 0 }), 200));
    },
  });

  router.beforeEach((to, _, next) => {
    i18n.global.locale.value = to.meta.locale as "fr" | "en";
    next();
  });

  return router;
}
